import { OverlayModule } from '@angular/cdk/overlay';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageRecordDialogComponent } from '@shared/localization';
import { ConfirmationDialogComponent, InfiniteScrollDirective, VerticalExpandAnimation } from '@shared/ui';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { SaveLabelLanguage } from '@telespot/domain';

import { EDITOR_DIALOG_MEDIUM } from '../../editor-dialog-sizes';
import { LabelData } from '../../../state/label.state';
import {
  LabelConfigDialogResult,
  LabelConfigEditorDialogComponent,
  LabelConfigEditorDialogData,
} from '../../labels/label-config-editor/label-config-editor-dialog.component';
import {
  CategoryOptionsManagementListComponent,
  OptionListItem,
} from '../category-options-management-list/category-options-management-list.component';
import {
  LabelThresholdDialogComponent,
  LabelThresholdDialogData,
} from '../label-threshold-dialog/label-threshold-dialog.component';

import { PipelineCategoryEditorFacade } from './pipeline-category-editor.facade';

@Component({
  selector: 'ts-pipeline-category-editor',
  templateUrl: './pipeline-category-editor.component.html',
  styleUrls: ['./pipeline-category-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    OverlayModule,
    CategoryOptionsManagementListComponent,
    InfiniteScrollDirective,
  ],
  animations: [VerticalExpandAnimation],
})
export class PipelineCategoryEditorComponent implements OnInit, OnDestroy {
  public readonly categoryControl = new FormControl<string>('', { nonNullable: true });

  public readonly canSubmit$ = this.facade.canSubmit$;

  public categorySuggestions: LabelData[] = [];
  public categorySearchTerm = '';
  public highlightedIndex: number | null = null;
  public isDropdownOpen = false;

  /**
   * Connected-overlay positions for the suggestion panel: prefer dropping below
   * the input, fall back to opening upwards when there is no room. The 4px
   * offset mirrors the gap the panel used to get from its `margin-top`.
   */
  public readonly suggestionsOverlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private readonly destroy$ = new Subject<void>();

  /**
   * Re-entrancy latch for the label-config dialog, mirroring the threshold
   * dialog's local handling: the dialog owns its own session, so nothing about
   * the open needs to travel through `PipelineCategoryState`.
   */
  private configDialogOpen = false;

  constructor(
    public readonly facade: PipelineCategoryEditorFacade,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.facade.initializeAutocompletes();

    this.facade.categoryDisplayName$.pipe(takeUntil(this.destroy$)).subscribe((name) => {
      if (name && this.categoryControl.value !== name) {
        this.categoryControl.setValue(name, { emitEvent: false });
      }
    });

    this.facade.suggestions$.pipe(takeUntil(this.destroy$)).subscribe((labels) => {
      this.categorySuggestions = labels;
      this.highlightedIndex = null;

      // OnPush + assignment from an async query result: nothing in the template
      // is bound to suggestions$ via | async, so mark the view dirty manually
      // or the dropdown stays stale until the next DOM event.
      this.cdr.markForCheck();
    });

    this.facade.categoryInput$.pipe(takeUntil(this.destroy$)).subscribe((term) => {
      this.categorySearchTerm = term ?? '';
      this.highlightedIndex = null;
    });

    this.categoryControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((term) => {
      this.isDropdownOpen = true;
      this.facade.searchCategory(term ?? '');
    });

    this.facade.readonly$.pipe(takeUntil(this.destroy$)).subscribe((readonly) => {
      if (readonly) {
        this.categoryControl.disable({ emitEvent: false });
      } else {
        this.categoryControl.enable({ emitEvent: false });
      }
    });

    this.facade.categoryFieldError$.pipe(takeUntil(this.destroy$)).subscribe((hasError) => {
      if (hasError) {
        this.categoryControl.markAsTouched();
        this.categoryControl.setErrors({ categoryRequired: true });
      } else {
        this.categoryControl.setErrors(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.facade.resetFieldInteractionState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackByUuid(_: number, item: { uuid: string }): string {
    return item.uuid;
  }

  @ViewChild('categoryInput') private categoryInputRef!: ElementRef<HTMLInputElement>;

  public onCategoryPicked(label: LabelData): void {
    this.facade.pickCategory(label);
    this.categoryInputRef.nativeElement.blur();
  }

  public onCategorySubmitFreeText(): void {
    const term = (this.categoryControl.value ?? '').trim();
    if (!term) return;
    this.facade.submitCategoryFreeText(term);
    this.categoryInputRef.nativeElement.blur();
  }

  public onCategoryFocus(): void {
    this.facade.markCategoryFieldFocused();
    const value = (this.categoryControl.value ?? '').trim();
    if (!value) return;
    this.isDropdownOpen = true;
    this.facade.searchCategory(this.categoryControl.value);
  }

  public onCategoryBlur(): void {
    this.isDropdownOpen = false;
    this.highlightedIndex = null;
    this.facade.clearLabelSearch();
    this.facade.markCategoryFieldBlurred();
  }

  public onCategoryArrowDown(event: Event): void {
    if (!this.isDropdownOpen || this.categorySearchTerm.length === 0) return;
    event.preventDefault();
    const count = this.categorySuggestions.length + 1;
    this.highlightedIndex = this.highlightedIndex === null ? 0 : Math.min(this.highlightedIndex + 1, count - 1);
  }

  public onCategoryArrowUp(event: Event): void {
    if (this.highlightedIndex === null) return;
    event.preventDefault();
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
  }

  public onCategoryEscape(): void {
    this.categoryInputRef.nativeElement.blur();
  }

  public onCategoryEnter(): void {
    if (this.highlightedIndex !== null && this.highlightedIndex < this.categorySuggestions.length) {
      this.onCategoryPicked(this.categorySuggestions[this.highlightedIndex]);
    } else {
      this.onCategorySubmitFreeText();
    }
  }

  public onCategoryReachedBottom(): void {
    this.facade.loadMoreLabels(undefined);
  }

  public onOptionReachedBottom(): void {
    this.facade.loadMoreLabels(undefined);
  }

  public onOptionSearchTermChanged(term: string): void {
    this.facade.searchOption(term);
  }

  public onOptionPicked(label: LabelData): void {
    this.facade.pickOption(label);
  }

  public onOptionFreeTextSubmitted(term: string): void {
    this.facade.submitOptionFreeText(term);
  }

  public onOptionFocused(term: string): void {
    if (!term.trim()) return;
    this.facade.searchOption(term);
  }

  public onOptionBlurred(): void {
    this.facade.clearLabelSearch();
  }

  public onOptionRemoveConfirmed(option: OptionListItem): void {
    this.facade.removeOption(option.uuid);
  }

  public onOptionThresholdClicked(option: OptionListItem): void {
    this.facade.options$.pipe(take(1), takeUntil(this.destroy$)).subscribe((options) => {
      const target = options.find((o) => o.uuid === option.uuid);
      if (!target) return;
      this.dialog
        .open<LabelThresholdDialogComponent, LabelThresholdDialogData, number | undefined>(
          LabelThresholdDialogComponent,
          { width: '400px', hasBackdrop: true, data: { threshold: target.thrdis } }
        )
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((thrdis) => {
          if (thrdis === undefined) return;
          this.facade.updateOptionThreshold({ uuid: option.uuid, thrdis });
        });
    });
  }

  /**
   * Opens the multi-assign colour dialog for this option's label. The dialog
   * runs its own session and stages the picked colours straight into the shared
   * `LabelConfigState`, so nothing comes back on close — the staged rows ride
   * the page-level save cascade like every other edit here.
   *
   * `injector: this.injector` is what puts the shared ledger (and the cascade)
   * in the dialog's reach; the dialog only provides its own session stack.
   *
   * The session context is read ONCE here rather than bound: the dialog takes
   * plain `MAT_DIALOG_DATA`, and a snapshot at click time is exactly what it
   * needs (the case-type staging cannot change while the dialog is open).
   *
   * That snapshot also carries the method type this session is editing, when it
   * has one — the dialog then opens pre-checked and pre-coloured on it. On the
   * standalone pipeline routes there is none and the field is simply absent, so
   * the dialog opens blank. No route check: it follows from whether a MethodType
   * session state is in scope.
   */
  public onOptionColorClicked(option: OptionListItem): void {
    if (this.configDialogOpen) return;

    this.facade.labelConfigDialogContext$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(({ methodTypes, contextMethodTypeId }) => {
        // Latched here rather than before the read, so the flag's lifetime is
        // exactly the dialog's: a read that never emits (component torn down)
        // cannot leave the colour action permanently dead. The context emits
        // synchronously on subscribe, so re-entrancy is still covered.
        this.configDialogOpen = true;

        this.dialog
          .open<LabelConfigEditorDialogComponent, LabelConfigEditorDialogData, LabelConfigDialogResult>(
            LabelConfigEditorDialogComponent,
            {
              ...EDITOR_DIALOG_MEDIUM,
              hasBackdrop: true,
              injector: this.injector,
              data: { labelUuid: option.uuid, contextMethodTypes: methodTypes, contextMethodTypeId },
            }
          )
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe(() => (this.configDialogOpen = false));
      });
  }

  public onOptionsReordered(items: OptionListItem[]): void {
    this.facade.reorderOptions(items.map((item) => item.uuid));
  }

  public onOptionTranslateClicked(option: OptionListItem): void {
    this.facade.options$.pipe(take(1), takeUntil(this.destroy$)).subscribe((options) => {
      const target = options.find((o) => o.uuid === option.uuid);
      if (!target) return;
      const data = Object.fromEntries(
        Object.entries(target.name).filter(([, v]) => typeof v === 'string' && v !== '')
      ) as Record<string, string>;
      this.dialog
        .open(LanguageRecordDialogComponent, { width: '400px', hasBackdrop: true, data: { values: data } })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((values) => {
          if (!values) return;
          this.facade.translateOption({ uuid: option.uuid, values });
        });
    });
  }

  public onCategoryTranslateClicked(): void {
    this.facade.category$.pipe(take(1), takeUntil(this.destroy$)).subscribe((category) => {
      if (!category) return;
      const data = Object.fromEntries(
        Object.entries(category.name).filter(([, v]) => typeof v === 'string' && v !== '')
      ) as Record<string, string>;
      this.dialog
        .open(LanguageRecordDialogComponent, { width: '400px', hasBackdrop: true, data: { values: data } })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((values) => {
          if (!values) return;
          this.facade.translateCategory(values);
        });
    });
  }

  public onSave(): void {
    this.facade.save();
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.confirmDiscard$()
        .pipe(takeUntil(this.destroy$))
        .subscribe((discard) => {
          if (discard) this.facade.cancel();
        });
    });
  }

  /**
   * Exit gate for navigation-driven dismissal (browser back while the hosting
   * dialog is open): clean sessions exit silently; dirty sessions ask for
   * confirmation and, when the user discards, run the real cancel flow so the
   * pending-label ledger is cleared and the dialog closes through `status$`.
   */
  public canDeactivate(): Observable<boolean> | boolean {
    return this.facade.hasChanges$.pipe(
      take(1),
      switchMap((hasChanges) => {
        if (!hasChanges) return of(true);
        return this.confirmDiscard$().pipe(
          map((discard) => {
            if (discard) this.facade.cancel();
            return discard;
          })
        );
      })
    );
  }

  private confirmDiscard$(): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'pipeline_step_editor.warnings.cancel_unsaved.title',
          text: 'pipeline_step_editor.warnings.cancel_unsaved.text',
          acceptButtonText: 'button.discard_changes',
          cancelButtonText: 'pipeline_step_editor.button.keep_editing',
          warningDialog: true,
          defaultAction: 'cancel',
        },
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(
        take(1),
        map((result) => result === 'accept')
      );
  }

  public preferredName(label: LabelData): string {
    return label.languages.en ?? Object.values(label.languages).find((v): v is string => !!v) ?? '';
  }

  public matchedLanguage(label: LabelData, term: string): string {
    const lower = (term ?? '').trim().toLowerCase();
    if (lower) {
      const entry = (Object.entries(label.languages) as [SaveLabelLanguage, string | undefined][]).find(
        ([, v]) => typeof v === 'string' && v.toLowerCase().includes(lower)
      );
      if (entry) return entry[0];
    }
    if (label.languages.en !== undefined) return 'en';
    const first = (Object.keys(label.languages) as SaveLabelLanguage[])[0];
    return first ?? 'en';
  }

  public matchedValue(label: LabelData, term: string): string {
    const lower = (term ?? '').trim().toLowerCase();
    if (lower) {
      const entry = Object.values(label.languages).find(
        (v): v is string => typeof v === 'string' && v.toLowerCase().includes(lower)
      );
      if (entry) return entry;
    }
    return this.preferredName(label);
  }
}
